// =============================================================================
// TYPES POUR LA VENTILATION DU BUDGET PAR CATÉGORIE
// =============================================================================

// Clés des catégories de budget
export type BudgetCategory = 'main_oeuvre' | 'materiaux' | 'sous_traitance' | 'frais_annexes';

// Structure de la ventilation du budget (stockée en JSONB)
export interface BudgetVentile {
  main_oeuvre: number;
  materiaux: number;
  sous_traitance: number;
  frais_annexes: number;
}

// Dépenses réelles par catégorie (retour de get_depenses_par_categorie)
export interface DepensesParCategorie {
  main_oeuvre: number;
  materiaux: number;
  sous_traitance: number;
  frais_annexes: number;
  total: number;
}

// Comparaison budget vs réel pour une catégorie
export interface BudgetCategoryComparison {
  prevu: number;      // Budget alloué à la catégorie
  reel: number;       // Dépenses réelles
  ecart: number;      // Différence (positif = sous budget, négatif = dépassement)
  pourcentage: number; // % consommé (0-100+)
}

// Réponse complète de get_budget_vs_reel
export interface BudgetVsReel {
  has_ventilation: boolean;
  budget_total: number;
  depenses_total: number;
  budget_ventile_total?: number | null;
  categories?: {
    main_oeuvre: BudgetCategoryComparison;
    materiaux: BudgetCategoryComparison;
    sous_traitance: BudgetCategoryComparison;
    frais_annexes: BudgetCategoryComparison;
  } | null;
}

// Statut visuel d'une catégorie
export type BudgetCategoryStatus = 'confortable' | 'attention' | 'critique' | 'non_defini';

// Configuration des catégories pour l'UI
export interface CategoryConfig {
  key: BudgetCategory;
  label: string;
  icon: string;
  color: string;
}

// Liste des catégories avec leurs configurations
export const CATEGORIES_BUDGET: CategoryConfig[] = [
  { key: 'main_oeuvre', label: "Main d'oeuvre", icon: '👷', color: '#3B82F6' },
  { key: 'materiaux', label: 'Matériaux', icon: '📦', color: '#10B981' },
  { key: 'sous_traitance', label: 'Sous-traitance', icon: '🏗️', color: '#F59E0B' },
  { key: 'frais_annexes', label: 'Frais annexes', icon: '📋', color: '#8B5CF6' },
];

// Labels pour les alertes et affichages
export const CATEGORY_LABELS: Record<BudgetCategory, string> = {
  main_oeuvre: "Main d'oeuvre",
  materiaux: 'Matériaux',
  sous_traitance: 'Sous-traitance',
  frais_annexes: 'Frais annexes',
};

// Seuils pour les indicateurs visuels
export const SEUILS_BUDGET_CATEGORIE = {
  confortable: 70,  // < 70% = vert
  attention: 90,    // 70-90% = orange
  critique: 90,     // > 90% = rouge
};

// Fonction utilitaire pour déterminer le statut d'une catégorie
export function getCategoryStatus(pourcentage: number): BudgetCategoryStatus {
  if (pourcentage > SEUILS_BUDGET_CATEGORIE.critique) return 'critique';
  if (pourcentage >= SEUILS_BUDGET_CATEGORIE.confortable) return 'attention';
  return 'confortable';
}

// Fonction utilitaire pour obtenir la couleur selon le statut
export function getStatusColor(status: BudgetCategoryStatus): string {
  switch (status) {
    case 'confortable':
      return '#10B981'; // emerald-500
    case 'attention':
      return '#F59E0B'; // amber-500
    case 'critique':
      return '#EF4444'; // red-500
    case 'non_defini':
      return '#6B7280'; // gray-500
  }
}

// Fonction utilitaire pour obtenir les classes CSS selon le statut
export function getStatusClasses(status: BudgetCategoryStatus): {
  text: string;
  bg: string;
  border: string;
} {
  switch (status) {
    case 'confortable':
      return {
        text: 'text-emerald-500',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/20',
      };
    case 'attention':
      return {
        text: 'text-amber-500',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/20',
      };
    case 'critique':
      return {
        text: 'text-red-500',
        bg: 'bg-red-500/10',
        border: 'border-red-500/20',
      };
    case 'non_defini':
      return {
        text: 'text-muted-foreground',
        bg: 'bg-muted/30',
        border: 'border-muted',
      };
  }
}
